import { useEffect, useMemo, useState } from 'react';
import { Alert, FormControl, HelpBlock } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';
import { IOpoDocument, IOpoSaveResult } from '@nkr/models/opo-document';
import { IOpoProfile, IOpoProfileRequest, IOptnRegionOption } from '@nkr/models/opo-profile';
import DocumentRow from '../document-row';

const OPO_W9 = 122;
const OPO_CMS_GOOD_STANDING = 123;
const OPO_SALES_TAX_EXEMPTION = 124;
const OPO_BANK_ACCOUNT_VERIFICATION = 125;

const timezones = [
    { value: 'PST', display: 'PST' },
    { value: 'MST', display: 'MST' },
    { value: 'PHO', display: 'MST - No DST' },
    { value: 'CST', display: 'CST' },
    { value: 'EST', display: 'EST' },
    { value: 'HST', display: 'HST' }
];

const isWebUrl = (value: string) => {
    try {
        const url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`);
        return (url.protocol == 'http:' || url.protocol == 'https:') && url.hostname.includes('.');
    } catch {
        return false;
    }
};

const profileSchema = z.object({
    optnName: z.string().trim().min(1, 'OPO Name is required').max(64, 'OPO Name cannot be longer than 64 characters'),
    optnAlias: z.string().trim().min(1, 'OPTN Alias is required').max(32, 'OPTN Alias cannot be longer than 32 characters'),
    legalName: z.string().trim().max(128, 'Legal Name cannot be longer than 128 characters'),
    website: z.string().trim().max(64, 'Web URL cannot be longer than 64 characters')
        .refine(value => value == '' || isWebUrl(value), 'Web URL must be a valid web address'),
    opoRegion: z.string(),
    timezone: z.string(),
    isTaxExempt: z.boolean().nullable(),
    taxExemptExpirationDate: z.string(),
    achRoutingNumber: z.string().trim()
        .refine(value => value == '' || /^\d{9}$/.test(value), 'ABA Routing Number must be 9 digits'),
    achAccountNumber: z.string().trim()
        .refine(value => value == '' || value.startsWith('*') || /^\d{4,17}$/.test(value), 'Bank Account Number must be 4 to 17 digits'),
    documents: z.record(z.any())
}).refine(x => x.isTaxExempt != true || x.taxExemptExpirationDate != '', {
    message: 'Expiration date is required when the OPO is sales tax exempt',
    path: ['taxExemptExpirationDate']
});

type ProfileFormValues = z.infer<typeof profileSchema>;

const toFormValues = (profile: IOpoProfile): ProfileFormValues => ({
    optnName: profile.optnName,
    optnAlias: profile.optnAlias,
    legalName: profile.legalName,
    website: profile.website,
    opoRegion: profile.opoRegion ? String(profile.opoRegion) : '',
    timezone: profile.timezone,
    isTaxExempt: profile.isTaxExempt,
    taxExemptExpirationDate: profile.taxExemptExpirationDate || '',
    achRoutingNumber: profile.achRoutingNumber,
    achAccountNumber: profile.achAccountNumberMasked,
    documents: {}
});

type TextField = 'optnName' | 'optnAlias' | 'legalName' | 'website' | 'achRoutingNumber' | 'achAccountNumber';

interface IProps {
    profile: IOpoProfile,
    canEdit: boolean,
    documents: IOpoDocument[],
    regions: IOptnRegionOption[],
    onSave: (request: IOpoProfileRequest, documents: Record<number, File>) => Promise<IOpoSaveResult>,
    isRoutingNumberValid: (routingNumber: string) => Promise<boolean>,
    onDownloadDocument: (document: IOpoDocument) => void
}

const ProfileForm = ({ profile, canEdit, documents, regions, onSave, isRoutingNumberValid, onDownloadDocument }: IProps) => {
    const [serverError, setServerError] = useState('');

    const schema = useMemo(() => profileSchema.superRefine(async (values, ctx) => {
        const routingNumber = values.achRoutingNumber.trim();
        const accountNumber = values.achAccountNumber.trim();
        const achChanged = routingNumber != profile.achRoutingNumber || accountNumber != profile.achAccountNumberMasked;

        if (achChanged && (routingNumber != '' || accountNumber != '') && !values.documents[OPO_BANK_ACCOUNT_VERIFICATION]) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Select a Bank Account Verification Document for the new routing or account number', path: ['documents'] });
        }

        if (/^\d{9}$/.test(routingNumber) && routingNumber != profile.achRoutingNumber && !await isRoutingNumberValid(routingNumber)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid ABA Routing Number', path: ['achRoutingNumber'] });
        }
    }), [profile.achRoutingNumber, profile.achAccountNumberMasked]);

    const { control, handleSubmit, reset, watch, formState: { errors, isSubmitting, isDirty } } = useForm<ProfileFormValues>({
        resolver: zodResolver(schema),
        defaultValues: toFormValues(profile)
    });

    useEffect(() => {
        reset(toFormValues(profile));
    }, [profile]);

    const isTaxExempt = watch('isTaxExempt');

    const onSubmit = async (values: ProfileFormValues) => {
        setServerError('');

        const files: Record<number, File> = { ...values.documents };

        if (!values.isTaxExempt) {
            delete files[OPO_SALES_TAX_EXEMPTION];
        }

        const result = await onSave({
            optnName: values.optnName,
            optnAlias: values.optnAlias,
            legalName: values.legalName,
            website: values.website,
            opoRegion: values.opoRegion ? Number(values.opoRegion) : null,
            timezone: values.timezone,
            isTaxExempt: values.isTaxExempt,
            taxExemptExpirationDate: values.isTaxExempt ? values.taxExemptExpirationDate : null,
            achRoutingNumber: values.achRoutingNumber,
            achAccountNumber: values.achAccountNumber
        }, files);

        if (result.success) {
            toast.success('Profile saved');
        } else {
            setServerError(result.message);
        }
    };

    const renderRow = (label: string, content: JSX.Element, error?: string, htmlFor?: string) => (
        <div className={error ? 'settings-row has-error' : 'settings-row'}>
            <label className='control-label wrap' htmlFor={htmlFor}>{label}</label>
            <div>
                {content}
                {error && (
                    <HelpBlock>{error}</HelpBlock>
                )}
            </div>
        </div>
    );

    const renderTextRow = (name: TextField, label: string, maxLength: number, placeholder?: string) => renderRow(label, (
        <Controller
            control={control}
            name={name}
            render={({ field: { value, onChange, onBlur } }) => (
                <FormControl id={name} type='text' maxLength={maxLength} placeholder={placeholder} disabled={!canEdit} value={value} onChange={onChange} onBlur={onBlur} />
            )}
        />
    ), errors[name]?.message, name);

    const renderDocumentRow = (documentTypeId: number) => {
        const document = documents.find(x => x.documentTypeId == documentTypeId);

        return document ? renderRow(document.name, (
            <Controller
                control={control}
                name='documents'
                render={({ field: { value, onChange } }) => (
                    <DocumentRow
                        document={document}
                        canUpload={canEdit}
                        pendingFile={value[documentTypeId]}
                        onSelect={(x, file) => {
                            if (!/\.pdf$/i.test(file.name)) {
                                setServerError(`The ${x.name} must be a PDF`);
                                return;
                            }

                            setServerError('');
                            onChange({ ...value, [documentTypeId]: file });
                        }}
                        onDownload={onDownloadDocument}
                        onError={setServerError}
                    />
                )}
            />
        ), documentTypeId == OPO_BANK_ACCOUNT_VERIFICATION ? (errors.documents as { message?: string } | undefined)?.message : undefined) : null;
    };

    return (
        <form className='settings-card' onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className='settings-card-body'>
                {!canEdit && (
                    <div className='settings-notice'>Only OPO administrators can edit this profile.</div>
                )}

                {serverError && (
                    <Alert bsStyle='danger' onDismiss={() => setServerError('')}>{serverError}</Alert>
                )}

                {renderTextRow('optnName', 'OPO Name', 64)}
                {renderTextRow('optnAlias', 'OPTN Alias', 32)}
                {renderTextRow('legalName', 'Legal Name', 128)}
                {renderTextRow('website', 'Web URL', 64, 'https://')}

                {renderRow('OPO Region', (
                    <Controller
                        control={control}
                        name='opoRegion'
                        render={({ field: { value, onChange, onBlur } }) => (
                            <FormControl id='opoRegion' componentClass='select' disabled={!canEdit} value={value} onChange={onChange} onBlur={onBlur}>
                                <option value=''>Select a region...</option>
                                {regions.map(region =>
                                    <option key={region.id} value={region.id}>{region.regionNumber} - {region.states}</option>
                                )}
                            </FormControl>
                        )}
                    />
                ), errors.opoRegion?.message, 'opoRegion')}

                {renderRow('Time Zone', (
                    <Controller
                        control={control}
                        name='timezone'
                        render={({ field: { value, onChange, onBlur } }) => (
                            <FormControl id='timezone' componentClass='select' disabled={!canEdit} value={value} onChange={onChange} onBlur={onBlur} style={{ maxWidth: 180 }}>
                                <option value=''>Select...</option>
                                {timezones.map(timezone =>
                                    <option key={timezone.value} value={timezone.value}>{timezone.display}</option>
                                )}
                            </FormControl>
                        )}
                    />
                ), errors.timezone?.message, 'timezone')}

                {renderDocumentRow(OPO_W9)}
                {renderDocumentRow(OPO_CMS_GOOD_STANDING)}

                {/* {renderRow('ABA Routing Number', (
                    <>
                        <FormControl type='text' disabled={!canEdit} value={profile.achRoutingNumber} />
                    </>
                ))} */}

                {renderTextRow('achRoutingNumber', 'ABA Routing Number', 64)}
                {renderTextRow('achAccountNumber', 'Bank Account Number', 64)}

                {/* {renderRow('Bank Account Number', (
                    <FormControl type='text' disabled={!canEdit} value={profile.achAccountNumberMasked} />
                ))} */}

                {renderDocumentRow(OPO_BANK_ACCOUNT_VERIFICATION)}

                {renderRow('Is your OPO Sales Tax Exempt?', (
                    <Controller
                        control={control}
                        name='isTaxExempt'
                        render={({ field: { value, onChange } }) => (
                            <div className='segmented' role='radiogroup' aria-label='Is your OPO Sales Tax Exempt?'>
                                <button type='button' role='radio' aria-checked={value == true} disabled={!canEdit} className={value == true ? 'btn active' : 'btn'} onClick={() => onChange(true)}>Yes</button>
                                <button type='button' role='radio' aria-checked={value == false} disabled={!canEdit} className={value == false ? 'btn active' : 'btn'} onClick={() => onChange(false)}>No</button>
                            </div>
                        )}
                    />
                ))}

                {isTaxExempt == true && renderDocumentRow(OPO_SALES_TAX_EXEMPTION)}

                {isTaxExempt == true && renderRow('Exemption Certificate Expiration Date', (
                    <Controller
                        control={control}
                        name='taxExemptExpirationDate'
                        render={({ field: { value, onChange, onBlur } }) => (
                            <FormControl id='taxExemptExpirationDate' type='date' disabled={!canEdit} value={value} onChange={onChange} onBlur={onBlur} style={{ maxWidth: 180 }} />
                        )}
                    />
                ), errors.taxExemptExpirationDate?.message, 'taxExemptExpirationDate')}
            </div>

            {canEdit && (
                <div className='settings-card-footer'>
                    <button type='submit' className='btn btn-primary' disabled={isSubmitting || !isDirty}>Save Changes</button>
                    <button type='button' className='btn' disabled={isSubmitting || !isDirty} onClick={() => { reset(toFormValues(profile)); setServerError(''); }}>Cancel</button>
                </div>
            )}
        </form>
    );
}

export default ProfileForm;
